import dayjs from 'dayjs'
import React from 'react'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useAppearance } from '@fourthwall/appearance'
import { NumberString, getNumberString } from '@fourthwall/components'
import type { AnalyticsChartProps } from '@fourthwall/module-analytics'
import { Stat, mapChartToSeriesData, useMembershipsChartQuery } from '@fourthwall/module-analytics'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import { reverse, routing } from '@utils/routing'

import { getDateFormat, getFrom, getPrecision, getRangeQueryParam, getTo } from '../../utils'

type MembersTotalChartProps = {
  range: number
}

export const MembersTotalChart = ({ range }: MembersTotalChartProps) => {
  const { timezone, getCurrentShopTime, getCurrentRelativeShopTime } = useShopTime()
  const from = getFrom(getCurrentShopTime(), range)
  const to = getTo(getCurrentShopTime())
  const { appearance } = useAppearance()
  const { hasPermission } = useDashboardPermissions()
  const { currentShop } = useCurrentShop({ staleTime: Infinity })

  const { membershipsChartQuery: membersTotalQuery } = useMembershipsChartQuery([
    {
      from,
      to,
      precision: getPrecision(range),
      chartTypes: ['MEMBERS_TOTAL'],
      sid: [currentShop?.id!],
      zoneId: timezone,
    },
  ])

  const series: AnalyticsChartProps['series'] = [
    {
      label: 'Total members',
      data: mapChartToSeriesData(
        membersTotalQuery.data?.current.totalMembers?.data,
        {
          from,
          to,
          precision: getPrecision(range),
        },
        {
          allowPoints: false,
          now: getCurrentRelativeShopTime(),
        },
      ),
    },
  ]

  const chartTo = hasPermission('analytics.totalMembers')
    ? `${reverse(routing.analytics.overview.report.self, {
        reportType: 'memberships-total-members',
      })}?range=${getRangeQueryParam(range)}`
    : undefined

  return (
    <Stat
      appearance={appearance}
      label="Total members"
      loading={!membersTotalQuery.data || membersTotalQuery.isFetching}
      to={chartTo || ''}
      value={
        <NumberString value={membersTotalQuery.data?.current.totalMembers?.aggregatedValue || 0} />
      }
      chart={{
        formatDate: (date) => dayjs(date).format(getDateFormat(getPrecision(range))),
        formatValue: getNumberString,
        series,
      }}
    />
  )
}
